import { useState } from 'react'
import type { PropertyValue } from '../services/ObjectsService'
import { MultiSelectPropertyInput } from 'objects/property-forms/multi-select/MultiSelectPropertyInput.tsx'
import { Select1PropertyInput } from 'objects/property-forms/select-1/Select1PropertyInput.tsx'
import { NumberPropertyInput } from '@/objects/property-forms/NumberPropertyInput.tsx'
import { Box } from '@mui/material'
import { useObjectFormContext } from '../object-form/ObjectFormContext.tsx'
import { DateInput } from '@/objects/property-forms/DateInput.tsx'
import type { Dayjs } from 'dayjs'
import { TogglePropertyInput } from '@/objects/property-forms/TogglePropertyInput.tsx'
import { TextPropertyInput } from '@/objects/property-forms/TextPropertyInput.tsx'
import type { PropertyDefinitionModel } from '@/core/gql/gql-models-override.ts'

interface PropertyInputOuterProps {
  propDef: PropertyDefinitionModel
  initialValue: PropertyValue
  readonly?: boolean
  maxWidth?: number
}

export function PropertyInput({ propDef, initialValue, readonly, maxWidth = 200 }: PropertyInputOuterProps) {
  const controller = useObjectFormContext()
  const [value, setValue] = useState<PropertyValue>(initialValue)

  function handleChange(v: PropertyValue) {
    setValue(v)
    if (!readonly) controller.onPropValueChange(propDef.id, v)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: '60px',
        maxWidth: `${maxWidth}px`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderRadius: '2px',
        cursor: readonly ? 'default' : 'pointer',
        width: '100%',
        padding: 0.5,
        overflow: 'hidden',
        pointerEvents: readonly ? 'none' : undefined,
        ['&:hover']: {
          backgroundColor: readonly ? undefined : (theme) => theme.palette.action.hover,
        },
      }}
    >
      {inputForType()}
    </Box>
  )

  function inputForType() {
    switch (propDef.type) {
      case 'select-1':
        return (
          <Select1PropertyInput
            propDef={propDef}
            value={(value as number | null | undefined) ?? null}
            onValueChange={handleChange}
          />
        )
      case 'multi-select':
        return (
          <MultiSelectPropertyInput
            options={propDef.options ?? []}
            value={(value as number[] | null) ?? []}
            onValueChange={handleChange}
            editable={{ propDef }}
          />
        )
      case 'date':
        return (
          <DateInput
            value={(value as Dayjs | null | undefined) ?? null}
            onValueChange={handleChange}
          />
        )
      case 'toggle':
        return <TogglePropertyInput value={value as boolean} onValueChange={handleChange} />
      case 'number':
        return (
          <NumberPropertyInput
            value={(value as number | null | undefined) ?? null}
            onValueChange={handleChange}
          />
        )
      case 'text':
        return <TextPropertyInput value={value as string} onValueChange={handleChange} />
      default:
        return <p>Not Impl</p>
    }
  }
}
